import { Box, Modal } from "@mui/material";
import type { ResponsiveStyleValue } from "@mui/system";
import { forwardRef } from "react";
import { MOBILE_BREAKPOINT } from "../../styles/responsive";

// モーダルコンポーネントを一般化して、再利用可能にします。
interface ModalProps {
  isOpen: boolean;
  handleClose: (
    event: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ) => void;
  children: React.ReactNode;
  ariaLabelledby: string;
  ariaDescribedby: string;
  height?: number | string;
  // ブレークポイントごとに幅を変えたいケースがあるためレスポンシブ値も受け付ける
  width?: ResponsiveStyleValue<number | string>;
  maxHeight?: number | string;
  overflow?: "scroll" | "hidden" | "auto";
  /** false で背景タップ・Esc では閉じない（誤タップ対策）。省略時は従来どおり閉じる */
  closeOnBackdropClick?: boolean;
  /** true で SP では画面いっぱいに出す（一覧など中身が多いモーダル向け）。PC は width/height のまま */
  fullScreenOnMobile?: boolean;
}

export const CustomModal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      handleClose,
      children,
      ariaLabelledby,
      ariaDescribedby,
      height,
      maxHeight,
      width = 800,
      overflow,
      closeOnBackdropClick = true,
      fullScreenOnMobile = false,
    },
    ref
  ) => {
    return (
      <Modal
        open={isOpen}
        onClose={(event, reason) => {
          if (!closeOnBackdropClick) return;
          handleClose(event, reason);
        }}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
      >
        <Box
          ref={ref}
          sx={(theme) => ({
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height,
            maxHeight: maxHeight ?? "100%",
            width,
            // padding を幅に含め、SP でも画面幅を超えない
            boxSizing: "border-box",
            maxWidth: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 1,
            outline: "none",
            p: 4,
            overflow: overflow ?? "auto",
            // SP では画面いっぱい（PC の幅・高さ・中央寄せはそのまま）
            ...(fullScreenOnMobile && {
              [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
                top: 0,
                left: 0,
                transform: "none",
                width: "100%",
                height: "100%",
                maxHeight: "100%",
                borderRadius: 0,
                p: 2,
              },
            }),
          })}
        >
          {children}
        </Box>
      </Modal>
    );
  }
);

CustomModal.displayName = "CustomModal";
